import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BookOpen, Video, ClipboardList, Award, Check, Lock,
  ExternalLink, ChevronRight, Loader2, ArrowLeft,
  MapPin, CalendarDays, Mic, Users2, Clock,
} from 'lucide-react';
import { api, getUser } from '../lib/api';
import { CE_CREDIT_META, LIVE_ONLY_META, formatAgendaTime } from '../lib/agenda-pdf';
import AgendaItemSpeaker from '../components/AgendaItemSpeaker';
import MapLink from '../components/MapLink';

const STEPS = [
  { key: 'session',     label: 'Roadmap & Session', icon: Video,       progressKey: 'attendance_verified_at' },
  { key: 'evaluation',  label: 'Evaluation',        icon: ClipboardList, progressKey: 'evaluation_submitted_at' },
  { key: 'certificate', label: 'Certificate',       icon: Award,        progressKey: 'certificate_issued_at' },
];

export default function LiveCoursePlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    if (!getUser()) { navigate(`/login?next=/courses/${slug}/live`); return; }

    let cancelled = false;
    (async () => {
      try {
        const enrolls = await api('/api/me/enrollments');
        const found = enrolls.enrollments?.find((e) => e.course_slug === slug);
        if (!found) { if (!cancelled) setError('Not enrolled in this course.'); return; }

        const progress = await api(`/api/live/${found.id}/progress`);
        if (!cancelled) {
          setData(progress);
          setActiveStep(getCurrentStep(progress.progress));
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-xl font-semibold text-red-600">Can't open course</h1>
        <p className="mt-2 text-slate-600">{error}</p>
        <button onClick={() => navigate('/dashboard')} className="mt-4 text-brand underline">Back to dashboard</button>
      </div>
    );
  }

  if (!data) return <div className="max-w-2xl mx-auto px-4 py-12 text-slate-500">Loading course…</div>;

  const stepProps = {
    data,
    setData,
    onAdvance: () => setActiveStep((s) => Math.min(s + 1, STEPS.length - 1)),
  };

  return (
    <div className="fixed inset-0 bg-slate-50 z-50 flex flex-col">
      {/* ── Top bar ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-2 bg-slate-900 text-white text-sm shrink-0">
        <span className="font-medium">{data.course.title}</span>
        <button onClick={() => navigate('/dashboard')} className="text-slate-300 hover:text-white flex items-center gap-1">
          <ArrowLeft size={14} /> Exit course
        </button>
      </div>

      {/* ── Step navigation ──────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-4 py-3 shrink-0">
        <div className="max-w-3xl mx-auto flex items-center gap-1">
          {STEPS.map((step, i) => {
            const done = !!data.progress[step.progressKey];
            const current = i === activeStep;
            const locked = i > 0 && !data.progress[STEPS[i - 1].progressKey];
            const Icon = step.icon;
            return (
              <button
                key={step.key}
                onClick={() => !locked && setActiveStep(i)}
                disabled={locked}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all flex-1 justify-center
                  ${current ? 'bg-brand/10 text-brand ring-1 ring-brand/30' : ''}
                  ${done && !current ? 'text-emerald-600' : ''}
                  ${locked ? 'text-slate-300 cursor-not-allowed' : ''}
                  ${!done && !current && !locked ? 'text-slate-500 hover:bg-slate-50' : ''}`}
              >
                <span className="shrink-0">
                  {done ? <Check size={16} className="text-emerald-500" /> : locked ? <Lock size={14} /> : <Icon size={16} />}
                </span>
                <span className="hidden sm:inline">{step.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Step content ─────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-8">
          {activeStep === 0 && <SessionStep {...stepProps} />}
          {activeStep === 1 && <EvaluationStep {...stepProps} />}
          {activeStep === 2 && <CertificateStep {...stepProps} />}
        </div>
      </div>
    </div>
  );
}

function getCurrentStep(progress) {
  if (!progress.attendance_verified_at) return 0;
  if (!progress.evaluation_submitted_at) return 1;
  return 2;
}

// Roadmap is authored as three structured sections (Description, Objectives,
// Course Flow). Older courses only have the legacy single `live_roadmap_md`
// blob — fall back to rendering that as plain text.
const ROADMAP_SECTIONS = [
  { key: 'live_roadmap_description', label: 'Description' },
  { key: 'live_roadmap_objectives',  label: 'Objectives' },
  { key: 'live_roadmap_flow',        label: 'Course Flow' },
];

function RoadmapContent({ course }) {
  const sections = ROADMAP_SECTIONS
    .map((s) => ({ ...s, body: course[s.key] }))
    .filter((s) => s.body && s.body.trim());

  if (!sections.length) {
    return (
      <div className="prose prose-slate max-w-none whitespace-pre-wrap text-sm leading-relaxed">
        {course.live_roadmap_md || 'No roadmap content has been provided for this course.'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((s) => (
        <section key={s.key}>
          <h3 className="text-lg font-bold text-brand mb-2">{s.label}</h3>
          <div className="text-sm leading-relaxed text-slate-700">
            <RoadmapBody text={s.body} />
          </div>
        </section>
      ))}
    </div>
  );
}

// Lightweight renderer for a section body. Walks the text line by line and
// groups it into bullet lists (-, *, •), numbered lists (1. / 1)) and
// paragraphs. Consecutive list items merge into one list even when separated
// by blank lines — and numbered lists are renumbered by CSS, so whatever the
// author typed (all "1.", out of order, etc.) always renders sequentially.
function RoadmapBody({ text }) {
  const groups = [];
  let blankSeen = false;

  for (const raw of String(text).replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) { blankSeen = true; continue; }

    let type, content;
    if (/^[-*•]\s+/.test(line)) { type = 'ul'; content = line.replace(/^[-*•]\s+/, ''); }
    else if (/^\d+[.)]\s+/.test(line)) { type = 'ol'; content = line.replace(/^\d+[.)]\s+/, ''); }
    else { type = 'p'; content = line; }

    const last = groups[groups.length - 1];
    if (type === 'p' ? (last?.type === 'p' && !blankSeen) : last?.type === type) {
      last.items.push(content);
    } else {
      groups.push({ type, items: [content] });
    }
    blankSeen = false;
  }

  return groups.map((g, gi) => {
    if (g.type === 'ul') {
      return (
        <ul key={gi} className="list-disc pl-5 space-y-1 mb-3 last:mb-0">
          {g.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
        </ul>
      );
    }
    if (g.type === 'ol') {
      return (
        <ol key={gi} className="list-decimal pl-5 space-y-1 mb-3 last:mb-0">
          {g.items.map((it, i) => <li key={i}>{renderInline(it)}</li>)}
        </ol>
      );
    }
    return (
      <p key={gi} className="mb-3 last:mb-0">
        {g.items.map((it, i) => (
          <span key={i}>{renderInline(it)}{i < g.items.length - 1 && <br />}</span>
        ))}
      </p>
    );
  });
}

function renderInline(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    /^\*\*[^*]+\*\*$/.test(part)
      ? <strong key={i} className="font-semibold text-slate-900">{part.slice(2, -2)}</strong>
      : part
  );
}

// ── Hybrid event pieces ────────────────────────────────────

const SESSION_TYPE_STYLE = {
  keynote:  'bg-amber-100 text-amber-700',
  breakout: 'bg-violet-100 text-violet-700',
  break:    'bg-emerald-100 text-emerald-700',
  session:  'bg-slate-100 text-slate-600',
};

// The published schedule. Breakouts are informational — attendees see what's on
// and turn up; there is nothing to sign up for.
function AgendaPanel({ agenda, venue }) {
  const days = [...new Set(agenda.map((i) => i.day_number || 1))].sort((a, b) => a - b);
  const multiDay = days.length > 1;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
      <h3 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
        <CalendarDays size={17} className="text-brand" /> Agenda
      </h3>
      <div className="space-y-5">
        {days.map((day) => (
          <div key={day}>
            {multiDay && (
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                {agenda.find((i) => (i.day_number || 1) === day)?.day_label || `Day ${day}`}
              </div>
            )}
            <ul className="space-y-2.5">
              {agenda.filter((i) => (i.day_number || 1) === day).map((item) => (
                <li key={item.id} className="flex gap-3">
                  <div className="text-xs text-slate-500 tabular-nums w-32 shrink-0 pt-1">
                    {formatAgendaTime(item) || '—'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${SESSION_TYPE_STYLE[item.session_type] || SESSION_TYPE_STYLE.session}`}>
                        {item.session_type}
                      </span>
                      {CE_CREDIT_META[item.ce_credit] && (
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${CE_CREDIT_META[item.ce_credit].pill}`}>
                          {CE_CREDIT_META[item.ce_credit].label}
                        </span>
                      )}
                      {item.live_only && (
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${LIVE_ONLY_META.pill}`}>
                          {LIVE_ONLY_META.label}
                        </span>
                      )}
                      <span className="text-sm font-medium text-slate-900">{item.title}</span>
                    </div>
                    <AgendaItemSpeaker item={item} venue={venue} />
                    {item.description && <p className="text-xs text-slate-600 mt-1">{item.description}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// In-person attendance verification. Each check-in window's QR links here with
// its (never displayed) code, which is submitted automatically; anyone who
// can't scan is checked in by an event admin from the roster, which reaches the
// same attendance_verified_at gate.
function CheckInPanel({ data, setData }) {
  const windows = data.course.checkin_windows || [];
  const covered = new Set((data.checkins || []).map((c) => c.window_id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(null);
  const autoTried = useRef(false);

  const submit = async (value) => {
    const submitted = String(value ?? '').trim();
    if (!submitted) return;
    setBusy(true); setError(null); setNote(null);
    try {
      const res = await api(`/api/live/${data.enrollment_id}/check-in`, {
        method: 'POST', body: { code: submitted },
      });
      setData((d) => ({
        ...d,
        checkins: [...(d.checkins || []), { window_id: res.window_id, checked_in_at: res.checked_in_at, checkin_method: 'self' }],
        progress: {
          ...d.progress,
          attendance_verified_at: res.verified ? res.checked_in_at : d.progress.attendance_verified_at,
          checked_in_at: res.checked_in_at,
          checkin_method: 'self',
        },
      }));
      if (!res.verified && res.required_total > 1) {
        setNote(`Checked in for ${res.window_label}. ${res.covered} of ${res.required_total} — come back and check in again for the rest of the day.`);
      }
    } catch (err) {
      const e = err.body?.error;
      setError(
        e === 'invalid_code' ? "That QR code isn't a check-in for this event. Scan the sign again, or see an event admin."
        : e === 'window_not_open_yet' ? `${err.body.window_label} check-in isn't open yet.`
        : e === 'window_closed' ? `${err.body.window_label} check-in has closed — see an event admin.`
        : 'Check-in failed. Please see an event admin.',
      );
    } finally { setBusy(false); }
  };

  // A scanned QR lands here with ?checkin=CODE — submit it once, automatically,
  // so scanning is a single action rather than scan-then-tap.
  useEffect(() => {
    if (autoTried.current) return;
    const fromQr = new URLSearchParams(window.location.search).get('checkin');
    if (!fromQr) return;
    autoTried.current = true;
    // Drop it from the URL so a refresh or a shared link doesn't resubmit.
    window.history.replaceState({}, '', window.location.pathname);
    submit(fromQr);
  }, []);

  // Conference events check in on the public event page by scanning the QR at
  // the venue — no code to type here. Credit lands after the event, when the
  // scans are reconciled against registrations.
  if (data.course.conference_checkin) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Users2 size={16} className="text-brand" />
          <span className="font-medium text-slate-800">Check in at the venue</span>
        </div>
        <p className="text-sm text-slate-500 mb-3">
          Scan the <strong>Check in</strong> QR code when you arrive and the <strong>Check out</strong> QR
          code before you leave, then enter the email you registered with. No login or code needed.
          Your attendance is credited after the event.
        </p>
        <ul className="space-y-1.5 mb-3">
          {windows.map((w) => {
            const done = covered.has(w.id);
            return (
              <li key={w.id} className="flex items-center gap-2 text-sm">
                {done
                  ? <Check size={15} className="text-emerald-600 shrink-0" />
                  : <div className="w-[15px] h-[15px] rounded-full border-2 border-slate-300 shrink-0" />}
                <span className={done ? 'text-slate-500' : 'text-slate-800 font-medium'}>{w.label}</span>
                <span className="text-xs text-slate-400">
                  {done ? 'credited' : `${fmtClock(w.opens_at)}–${fmtClock(w.closes_at)}`}
                </span>
              </li>
            );
          })}
        </ul>
        <p className="text-xs text-slate-500">
          Can't scan? See an event admin at the registration table.
        </p>
      </div>
    );
  }

  if (!data.course.self_checkin_enabled) {
    return (
      <div className="text-sm text-slate-600">
        <div className="flex items-center gap-2 mb-2">
          <Users2 size={16} className="text-brand" />
          <span className="font-medium">Check in at the event</span>
        </div>
        <p className="text-slate-500">
          An event admin will check you in on site. Your evaluation and certificate unlock
          once that's done.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Users2 size={16} className="text-brand" />
        <span className="font-medium text-slate-800">Check in</span>
      </div>
      <p className="text-sm text-slate-500 mb-3">
        Scan the QR code on the sign at the venue with your phone camera
        {windows.length > 1 ? ' at each check-in, so your attendance covers the whole day' : ''}.
      </p>
      {windows.length > 0 && (
        <ul className="space-y-1.5 mb-3">
          {windows.map((w) => {
            const done = covered.has(w.id);
            return (
              <li key={w.id} className="flex items-center gap-2 text-sm">
                {done
                  ? <Check size={15} className="text-emerald-600 shrink-0" />
                  : <div className={`w-[15px] h-[15px] rounded-full border-2 shrink-0 ${w.state === 'open' ? 'border-brand' : 'border-slate-300'}`} />}
                <span className={done ? 'text-slate-500 line-through' : 'text-slate-800 font-medium'}>{w.label}</span>
                {!done && w.state === 'open' && <span className="text-xs text-brand font-medium">open now</span>}
                {!done && w.state === 'upcoming' && <span className="text-xs text-slate-400">opens {fmtClock(w.opens_at)}</span>}
                {!done && w.state === 'closed' && <span className="text-xs text-rose-500">closed</span>}
                {!w.required && <span className="text-xs text-slate-400">(optional)</span>}
              </li>
            );
          })}
        </ul>
      )}

      {busy && <div className="text-xs text-slate-500 flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Checking you in…</div>}
      {note && <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 mt-3">{note}</div>}
      {error && <div className="text-xs text-rose-600 mt-2">{error}</div>}
      <p className="text-xs text-slate-500 mt-3">
        Can't scan? An event admin can check you in.
      </p>
    </div>
  );
}

function fmtClock(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-US', {
    timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit',
  });
}

// ── Step 1: Roadmap + Live Session (combined) ──────────────

// After the scheduled end, a small "rejoin" link stays available this long in
// case the class runs over.
const REJOIN_GRACE_MS = 30 * 60 * 1000;
// If attendance still isn't verified this long after the scheduled end, stop
// implying it's on its way and tell the student how to get help.
const VERIFY_HELP_AFTER_MS = 60 * 60 * 1000;

function SessionStep({ data, setData, onAdvance }) {
  const done = !!data.progress.attendance_verified_at;
  // Someone attending in the room has no Zoom record, so the attendance poll
  // would never resolve for them — they check in instead.
  const isInPerson = data.attendance_mode === 'in_person';
  const [attendance, setAttendance] = useState(null);
  const pollRef = useRef(null);

  // Scheduled end of the class. Once it passes, the Join button is replaced by
  // a "class has ended" notice: students who still saw the button while waiting
  // for verification clicked it again, and each click opened a new, empty Zoom
  // session (THC 9/23).
  const endMs = data.course.live_date_utc
    ? new Date(data.course.live_date_utc).getTime() + (data.course.duration_minutes || 0) * 60_000
    : null;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (done || !endMs) return;
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, [done, endMs]);
  const classEnded = endMs != null && now >= endMs;
  const canRejoin = classEnded && now < endMs + REJOIN_GRACE_MS;
  const needsHelp = classEnded && now >= endMs + VERIFY_HELP_AFTER_MS;

  useEffect(() => {
    if (done || isInPerson) return;

    const poll = async () => {
      try {
        const res = await api(`/api/live/${data.enrollment_id}/attendance-status`);
        setAttendance(res);
        if (res.verified) {
          setData((d) => ({ ...d, progress: { ...d.progress, attendance_verified_at: res.verified_at } }));
          clearInterval(pollRef.current);
        }
      } catch (err) {
        console.error('Attendance poll failed:', err);
      }
    };

    poll();
    pollRef.current = setInterval(poll, 30_000);
    return () => clearInterval(pollRef.current);
  }, [done, isInPerson, data.enrollment_id, setData]);

  const zoomUrl = data.course.zoom_join_url
    || (data.course.zoom_meeting_id ? `https://zoom.us/j/${data.course.zoom_meeting_id}` : null);

  return (
    <div>
      <StepHeader icon={Video} title="Roadmap & Live Session" done={done} />

      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <RoadmapContent course={data.course} />
      </div>

      {/* Hybrid: where the event is, and the published schedule */}
      {isInPerson && (data.course.venue_name || data.course.venue_address) && (
        <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
          <div className="flex items-start gap-3">
            <MapPin size={18} className="text-brand shrink-0 mt-0.5" />
            <div>
              <span className="text-xs font-semibold uppercase text-slate-400">Venue</span>
              <MapLink query={[data.course.venue_name, data.course.venue_address]} className="block no-underline">
                {data.course.venue_name && <p className="underline decoration-brand/60 underline-offset-2">{data.course.venue_name}</p>}
                {data.course.venue_address && <p className="text-sm text-slate-600">{data.course.venue_address}</p>}
                <p className="text-xs font-semibold text-brand mt-1">Open in Maps →</p>
              </MapLink>
            </div>
          </div>
        </div>
      )}

      {data.course.agenda?.length > 0 && <AgendaPanel agenda={data.course.agenda} venue={data.course} />}

      <div className="bg-white rounded-lg border border-slate-200 p-6 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 text-sm mb-6">
          {data.course.live_date && (
            <div>
              <span className="text-xs font-semibold uppercase text-slate-400">Date</span>
              <p className="font-medium text-slate-800">{data.course.live_date}</p>
            </div>
          )}
          {data.course.duration_minutes && (
            <div>
              <span className="text-xs font-semibold uppercase text-slate-400">Duration</span>
              <p className="font-medium text-slate-800">{data.course.duration_minutes} minutes</p>
            </div>
          )}
          {data.course.hosted_by && (
            <div className="sm:col-span-2">
              <span className="text-xs font-semibold uppercase text-slate-400">Presenter</span>
              <p className="font-medium text-slate-800">
                {data.course.hosted_by}
                {data.course.hosted_by_title && <span className="text-slate-500 font-normal"> — {data.course.hosted_by_title}</span>}
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {zoomUrl && !isInPerson && !classEnded && (
            <a
              href={zoomUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#2D8CFF] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#2681EB] transition-colors"
            >
              <Video size={18} /> Join Zoom Meeting <ExternalLink size={14} />
            </a>
          )}
          {zoomUrl && !isInPerson && classEnded && !done && (
            <div className="w-full text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 font-medium text-slate-800">
                <Clock size={16} className="text-brand" /> The live class has ended
              </div>
              <p className="mt-1">
                There's no need to rejoin the Zoom meeting. We're confirming your attendance from Zoom's report,
                and this page will move you on to the evaluation by itself.
              </p>
              {canRejoin && (
                <p className="mt-2 text-xs text-slate-500">
                  Class running late and you got disconnected?{' '}
                  <a href={zoomUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-700">
                    Rejoin Zoom
                  </a>
                </p>
              )}
            </div>
          )}
          {isInPerson && (
            <div className="inline-flex items-center gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-4 py-2.5">
              <Users2 size={16} className="text-brand" />
              You're registered to attend <strong className="font-semibold text-slate-800">in person</strong>.
            </div>
          )}
        </div>
      </div>

      {/* Attendance status */}
      <div className="bg-white rounded-lg border border-slate-200 p-5">
        {!done && isInPerson ? (
          <CheckInPanel data={data} setData={setData} />
        ) : done ? (
          <DoneBar
            message={isInPerson
              ? 'Checked in — attendance verified'
              : `Attendance verified — ${attendance?.duration_minutes || '?'} minutes attended`}
            onNext={onAdvance}
            nextLabel="Go to Evaluation"
          />
        ) : needsHelp ? (
          <div className="text-sm text-slate-600">
            <p className="font-medium text-slate-800 mb-2">We couldn't confirm your attendance automatically</p>
            <p className="text-slate-500">
              {attendance?.duration_minutes > 0
                ? `Zoom recorded ${attendance.duration_minutes} minutes for you; this course needs ${attendance.required_minutes}. `
                : "We couldn't find you in the Zoom attendance report. "}
              If you attended the whole class, email{' '}
              <a href="mailto:support@learnableskill.com" className="underline">support@learnableskill.com</a>{' '}
              with the name and email you used in Zoom, and we'll verify you. This page checks every 30 seconds.
            </p>
          </div>
        ) : (
          <div className="text-sm text-slate-600">
            <div className="flex items-center gap-2 mb-2">
              <Loader2 size={16} className="animate-spin text-brand" />
              <span className="font-medium">Waiting for attendance verification…</span>
            </div>
            <p className="text-slate-500">
              Your attendance will be verified automatically after the session ends. This page checks every 30 seconds.
              {attendance && !attendance.verified && attendance.duration_minutes > 0 && (
                <span className="block mt-1">
                  Recorded so far: {attendance.duration_minutes} min (need {attendance.required_minutes} min)
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Step 3: Evaluation ──────────────────────────────────────

function EvaluationStep({ data, setData, onAdvance }) {
  const done = !!data.progress.evaluation_submitted_at;
  const questions = data.course.evaluation_questions || [];
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const updateAnswer = (qId, value) => setAnswers((a) => ({ ...a, [qId]: value }));

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const responses = Object.entries(answers).map(([question_id, value]) => ({ question_id, value }));
      const res = await api(`/api/live/${data.enrollment_id}/submit-evaluation`, {
        method: 'POST',
        body: { responses },
      });
      setData((d) => ({
        ...d,
        progress: { ...d.progress, evaluation_submitted_at: res.evaluation_submitted_at, certificate_issued_at: res.certificate ? new Date().toISOString() : d.progress.certificate_issued_at },
        certificate: res.certificate || d.certificate,
      }));
      onAdvance();
    } catch (err) {
      setError(err.body?.error || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const allRequiredAnswered = questions
    .filter((q) => q.required)
    .every((q) => {
      const a = answers[q.id];
      if (!a) return false;
      if (q.type === 'multi_select') {
        try { return JSON.parse(a).length > 0; } catch { return false; }
      }
      if (q.type === 'multi_select_text') {
        try { return (JSON.parse(a).selected || []).length > 0; } catch { return false; }
      }
      if (q.type === 'yes_no') {
        try { return !!JSON.parse(a).choice; } catch { return false; }
      }
      return String(a).trim().length > 0;
    });

  return (
    <div>
      <StepHeader icon={ClipboardList} title="Course Evaluation" done={done} />

      {done ? (
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <DoneBar message="Evaluation submitted — thank you!" onNext={onAdvance} nextLabel="View Certificate" />
        </div>
      ) : questions.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-6 text-sm text-slate-500">
          No evaluation questions have been configured for this course.
        </div>
      ) : (
        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={q.id} className="bg-white rounded-lg border border-slate-200 p-5">
              <label className="block text-sm font-medium text-slate-800 mb-3">
                {i + 1}. {q.prompt}
                {q.required && <span className="text-rose-500 ml-1">*</span>}
              </label>
              {q.type === 'likert' ? (
                <LikertInput value={answers[q.id]} onChange={(v) => updateAnswer(q.id, v)} />
              ) : q.type === 'multi_select' ? (
                <MultiSelectInput
                  options={q.options || []}
                  value={answers[q.id] || '[]'}
                  onChange={(v) => updateAnswer(q.id, v)}
                  allowOther={!!q.allow_other}
                />
              ) : q.type === 'multi_select_text' ? (
                <MultiSelectTextInput
                  options={q.options || []}
                  value={answers[q.id]}
                  onChange={(v) => updateAnswer(q.id, v)}
                  textLabel={q.text_label || 'Certification number'}
                />
              ) : q.type === 'yes_no' ? (
                <YesNoInput
                  value={answers[q.id]}
                  onChange={(v) => updateAnswer(q.id, v)}
                  explainLabel={q.explain_label || 'If yes, please explain'}
                />
              ) : (
                <textarea
                  rows={3}
                  value={answers[q.id] || ''}
                  onChange={(e) => updateAnswer(q.id, e.target.value)}
                  placeholder="Enter your response…"
                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none"
                />
              )}
            </div>
          ))}

          {error && (
            <div className="px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              onClick={submit}
              disabled={submitting || !allRequiredAnswered}
              className="inline-flex items-center gap-2 bg-brand text-white px-6 py-2.5 rounded-lg font-medium hover:bg-brand-dark disabled:opacity-50 transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
              Submit Evaluation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Emoji rating scale. Each face maps to a hidden 1–5 value (far left = 1,
// far right = 5) so scoring and the admin aggregates are unchanged.
function LikertInput({ value, onChange }) {
  const v = parseInt(value, 10);
  const FACES = [
    { n: 1, color: '#E24B4A', mouth: 'M7 16 Q12 11 17 16' },   // frown
    { n: 2, color: '#F59E0B', mouth: 'M7 15.5 Q12 13 17 15.5' },
    { n: 3, color: '#94A3B8', mouth: 'M7 15 L17 15' },          // neutral
    { n: 4, color: '#84CC16', mouth: 'M7 14.5 Q12 17 17 14.5' },
    { n: 5, color: '#22C55E', mouth: 'M7 14 Q12 18 17 14' },    // smile
  ];
  return (
    <div className="max-w-sm">
      <div className="grid grid-cols-5 justify-items-center gap-1">
        {FACES.map((f) => {
          const active = v === f.n;
          return (
            <button
              key={f.n}
              type="button"
              onClick={() => onChange(String(f.n))}
              aria-label={`Rating ${f.n} of 5`}
              aria-pressed={active}
              className={`rounded-full transition-transform ${active ? 'scale-110' : 'opacity-45 hover:opacity-90'}`}
            >
              <svg width="44" height="44" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke={f.color} strokeWidth="1.6" fill={active ? `${f.color}22` : 'transparent'} />
                <circle cx="9" cy="10" r="1.1" fill={f.color} />
                <circle cx="15" cy="10" r="1.1" fill={f.color} />
                <path d={f.mouth} stroke={f.color} strokeWidth="1.6" strokeLinecap="round" fill="none" />
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function YesNoInput({ value, onChange, explainLabel }) {
  let parsed = { choice: '', explanation: '' };
  try { if (value) parsed = { choice: '', explanation: '', ...JSON.parse(value) }; } catch { /* keep default */ }
  const emit = (next) => onChange(JSON.stringify({ choice: parsed.choice || '', explanation: parsed.explanation || '', ...next }));
  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        {['Yes', 'No'].map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => emit({ choice: opt })}
            className={`px-6 py-2 rounded-lg text-sm font-medium border transition-colors ${
              parsed.choice === opt
                ? 'bg-brand text-white border-brand'
                : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">{explainLabel}</label>
        <textarea
          rows={3}
          value={parsed.explanation || ''}
          onChange={(e) => emit({ explanation: e.target.value })}
          placeholder="Add details…"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand/30 focus:border-brand outline-none"
        />
      </div>
    </div>
  );
}

function MultiSelectInput({ options, value, onChange, allowOther }) {
  let selected = [];
  try { selected = JSON.parse(value); } catch {}

  const otherEntry = selected.find((s) => s.startsWith('Other: '));
  const otherChecked = !!otherEntry;
  const otherText = otherEntry ? otherEntry.slice(7) : '';

  const toggle = (opt) => {
    const next = selected.includes(opt)
      ? selected.filter((s) => s !== opt)
      : [...selected, opt];
    onChange(JSON.stringify(next));
  };

  const toggleOther = () => {
    if (otherChecked) {
      onChange(JSON.stringify(selected.filter((s) => !s.startsWith('Other: '))));
    } else {
      onChange(JSON.stringify([...selected, 'Other: ']));
    }
  };

  const setOtherText = (text) => {
    const without = selected.filter((s) => !s.startsWith('Other: '));
    onChange(JSON.stringify([...without, `Other: ${text}`]));
  };

  return (
    <div className="space-y-2">
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-slate-200 cursor-pointer hover:bg-slate-50 transition-colors">
          <input
            type="checkbox"
            checked={selected.includes(opt)}
            onChange={() => toggle(opt)}
            className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
          />
          <span className="text-sm text-slate-700">{opt}</span>
        </label>
      ))}
      {allowOther && (
        <div className="rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors">
          <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={otherChecked}
              onChange={toggleOther}
              className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
            />
            <span className="text-sm text-slate-700">Other</span>
          </label>
          {otherChecked && (
            <div className="px-3 pb-3 pl-10">
              <input
                type="text"
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                placeholder="Please specify…"
                className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                autoFocus
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Multi-select where each checked option reveals its own optional text field
// (e.g. select credential agencies, enter the certification number for each).
// Stored as { selected: [...], values: { [option]: text } }.
function MultiSelectTextInput({ options, value, onChange, textLabel }) {
  let parsed = { selected: [], values: {} };
  try { if (value) { const p = JSON.parse(value) || {}; parsed = { selected: p.selected || [], values: p.values || {} }; } } catch {}
  const { selected, values } = parsed;

  const emit = (nextSelected, nextValues) =>
    onChange(JSON.stringify({ selected: nextSelected, values: nextValues }));

  const toggle = (opt) => {
    if (selected.includes(opt)) {
      const nv = { ...values };
      delete nv[opt];
      emit(selected.filter((s) => s !== opt), nv);
    } else {
      emit([...selected, opt], values);
    }
  };

  const setText = (opt, text) =>
    emit(selected.includes(opt) ? selected : [...selected, opt], { ...values, [opt]: text });

  return (
    <div className="space-y-2">
      {options.map((opt) => {
        const checked = selected.includes(opt);
        return (
          <div key={opt} className={`rounded-lg border transition-colors ${checked ? 'border-brand/40 bg-brand/5' : 'border-slate-200'}`}>
            <label className="flex items-center gap-3 px-3 py-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggle(opt)}
                className="h-4 w-4 rounded border-slate-300 text-brand focus:ring-brand/30"
              />
              <span className="text-sm text-slate-700">{opt}</span>
            </label>
            {checked && (
              <div className="px-3 pb-3 pl-10">
                <input
                  type="text"
                  value={values[opt] || ''}
                  onChange={(e) => setText(opt, e.target.value)}
                  placeholder={`${textLabel} (optional)`}
                  className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Step 4: Certificate ─────────────────────────────────────

function CertificateStep({ data }) {
  const cert = data.certificate;
  const navigate = useNavigate();

  return (
    <div>
      <StepHeader icon={Award} title="Certificate" done={!!cert} />

      {cert ? (
        <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4">
            <Award size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Congratulations!</h2>
          <p className="text-slate-600 mb-1">You've completed <strong>{data.course.title}</strong>.</p>
          <p className="text-sm text-slate-500 mb-6">Certificate #{cert.certificate_number}</p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <a
              href={`/api/certificates/${cert.id}/download`}
              className="inline-flex items-center gap-2 bg-brand text-white px-5 py-2.5 rounded-lg font-medium hover:bg-brand-dark transition-colors"
            >
              Download Certificate
            </a>
            <button
              onClick={() => navigate(`/cert/${cert.certificate_number}`)}
              className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 px-5 py-2.5 rounded-lg font-medium hover:bg-slate-50 transition-colors"
            >
              <ExternalLink size={14} /> Verification Page
            </button>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 p-6 text-sm text-slate-500 flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Generating certificate…
        </div>
      )}
    </div>
  );
}

// ── Shared components ───────────────────────────────────────

function StepHeader({ icon: Icon, title, done }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${done ? 'bg-emerald-100 text-emerald-600' : 'bg-brand/10 text-brand'}`}>
        {done ? <Check size={20} /> : <Icon size={20} />}
      </div>
      <h2 className="text-xl font-bold text-slate-800">{title}</h2>
      {done && <span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">Complete</span>}
    </div>
  );
}

function DoneBar({ message, onNext, nextLabel }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-emerald-600 font-medium flex items-center gap-2">
        <Check size={16} /> {message}
      </span>
      {nextLabel && (
        <button
          onClick={onNext}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark transition-colors"
        >
          {nextLabel} <ChevronRight size={16} />
        </button>
      )}
    </div>
  );
}
